/**
 * Easy Mode Action Formatter
 * Transforms action descriptions to be more contextual for Easy Mode interface
 * where actions open windows rather than navigate to pages
 */

/**
 * Transforms action descriptions for Easy Mode context
 * Replaces "Navigate to" with "Open" or "Show" to better reflect window-based interface
 */
export function formatActionDescriptionForEasyMode(description: string, intent?: string): string {
  // Handle specific action intents with custom descriptions first
  if (intent === 'MANAGE_DATA_SEQUENCE') {
    return 'Open Data Handler';
  } else if (intent === 'OPEN_IMAGE_CANVAS') {
    return 'Open Image Canvas Designer';
  } else if (intent === 'GENERATE_IMAGE') {
    return 'Generate Image';
  } else if (intent === 'OPEN_RICH_TEXT_EDITOR') {
    return 'Open Text Editor';
  }

  // If no description provided, try to format the intent
  if (!description && intent) {
    // Convert intent to readable format (e.g., "OPEN_IMAGE_CANVAS" -> "Open Image Canvas")
    const readable = intent
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
    return readable;
  }

  if (!description) return description || 'Action';

  let formatted = description;

  // Replace "Navigate to" with "Open" for window-based actions
  formatted = formatted.replace(/^Navigate to\s+/i, 'Open ');
  formatted = formatted.replace(/^navigate to\s+/i, 'Open ');

  // Replace "go to" with "open"
  formatted = formatted.replace(/\bgo to\s+/gi, 'open ');

  // Replace "show" with "open" for consistency (but keep "show" if it's "show me" or similar)
  formatted = formatted.replace(/\bshow\s+(me\s+)?(the\s+)?/gi, 'open ');

  // Capitalize first letter
  if (formatted.length > 0) {
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  return formatted;
}

/**
 * Gets a contextual description based on the action intent and current context
 */
export function getContextualActionDescription(
  template: { description?: string; intent?: string },
  context?: { activeWindow?: string; isEasyMode?: boolean }
): string {
  const { description, intent } = template;
  const { isEasyMode = true } = context || {};

  if (!isEasyMode) {
    return description || intent || 'Action';
  }

  return formatActionDescriptionForEasyMode(description || '', intent);
}

